import type { AccountView } from "@kwp/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTRPC } from "../../lib/api";
import { useMe } from "../../lib/me";
import { activityInView, mergeEntries, type Entry } from "./activity-model";
import { hidesApps, useHiddenApps, withoutHidden } from "./hidden";
import { mailKindOf } from "./kinds";
import { boxOf } from "./mailboxes";
import { applyPatches, pickerSuggestions, type Thread } from "./model";
import { inboxConstraints, scopeAccountIds, searchScope, type InboxSearch } from "./scope";
import type { AccountInfo } from "./ThreadList";
import { useActivity } from "./use-activity";
import { useAccountTags } from "./use-mail-context";
import { useScope } from "./use-scope";
import { useMailActions } from "./use-mail-actions";
import { useGitHubSummary } from "./use-github";
import { sourceViewOf } from "./sources";
import { useThreads } from "./use-threads";
import { useThreadView } from "./ThreadView";

/**
 * Everything the inbox screen reads: the scope the URL names, the live thread list with the
 * optimistic patches applied, the open conversation, the accounts and the default From.
 */
export function useInbox(workspaceId: string, search: InboxSearch) {
  const me = useMe();
  const trpc = useTRPC();
  // A workspace scope resolves to accounts (§8), so it waits for the account list; undefined: not yet.
  const { scope, ids: scopeIds, saved, accounts } = useScope(workspaceId, search.scope);
  const idsKey = scopeIds === undefined ? null : JSON.stringify(scopeIds);
  const box = boxOf(search);
  // Which kind of mail the inbox lists (WP-061): All mail unless the URL names a kind (WP-065).
  const mailKind = mailKindOf(search.kind);
  // A known app's own view (WP-062): GitHub, a group, and one filter inside it.
  const sourceKey = JSON.stringify(box === "inbox" && search.filter === undefined ? sourceViewOf(search) : null);
  const source = useMemo(() => JSON.parse(sourceKey) as ReturnType<typeof sourceViewOf>, [sourceKey]);
  const constraints = useMemo(
    () => (idsKey === null ? null : inboxConstraints(me.uid, JSON.parse(idsKey) as string[] | null, box, search.filter, search.tag, mailKind, source)),
    [me.uid, idsKey, box, search.filter, search.tag, mailKind, source],
  );
  const threads = useThreads(constraints);
  // Apps the person keeps out of All mail (WP-066), taken out of the loaded pages here: a query
  // cannot leave them out, because mail with no app has no `source.app` for `!=` to match.
  const { hidden: hiddenApps, setHidden } = useHiddenApps(me.uid);
  const hiding = hidesApps(box, search.filter, mailKind, source !== null) && hiddenApps.length > 0 ? hiddenApps : null;
  // GitHub's counts, for the group tabs in its own view.
  const githubShown = search.q === undefined && source !== null;
  const listed = threads.status === "ready" ? threads.threads.map((t) => `${t.id}:${t.unread ? 1 : 0}`).join(",") : "";
  const github = useGitHubSummary(me.uid, idsKey === null ? [] : (JSON.parse(idsKey) as string[] | null), githubShown && idsKey !== null, listed);
  const actions = useMailActions();
  const { settle, reset } = actions;
  const viewKey = constraints === null ? null : JSON.stringify(constraints);
  useEffect(() => reset(), [viewKey, reset]);
  const loaded = threads.status === "ready" ? threads.threads : null;
  useEffect(() => {
    if (loaded !== null) settle(loaded);
  }, [loaded, settle]);
  const visible = useMemo(() => {
    if (loaded === null) return [];
    const patched = applyPatches(loaded, actions.patches);
    const rows = hiding === null ? patched : withoutHidden(patched, hiding);
    // The Snoozed view reads soonest first and the Sent view newest send first (the listener's
    // order), not newest mail first.
    if (box === "snoozed") {
      const wakes = (t: Thread) => (t.snoozedUntil === null ? Number.MAX_SAFE_INTEGER : t.snoozedUntil.getTime());
      return [...rows].sort((a, b) => wakes(a) - wakes(b) || a.id.localeCompare(b.id));
    }
    if (box === "sent") {
      const sent = (t: Thread) => (t.sentAt === null ? 0 : t.sentAt.getTime());
      return [...rows].sort((a, b) => sent(b) - sent(a) || a.id.localeCompare(b.id));
    }
    return rows;
  }, [loaded, actions.patches, box, hiding]);
  // Pages of only hidden mail leave nothing to scroll to, so the list reads on until it has rows.
  const { loadMore } = threads;
  const starved = hiding !== null && threads.status === "ready" && threads.hasMore && !threads.loadingMore && visible.length === 0;
  useEffect(() => {
    if (starved) loadMore();
  }, [starved, loadMore]);
  const activity = useActivity();
  const { unread } = activity;
  const activityItems = activity.status === "ready" ? activity.items : null;
  const moreMail = threads.status === "ready" && threads.hasMore;
  // Chats and tasks mixed with mail by time in All mail and People (WP-054, WP-065); a Chat or
  // Tasks view shows no mail, and a Notifications, Newsletters or Receipts list (WP-061) or a
  // source (WP-062) no chats or tasks.
  const activityKind = search.kind === "mail" || search.kind === "chat" || search.kind === "task" ? search.kind : undefined;
  const mailOnly = source !== null || (mailKind !== "person" && mailKind !== "all");
  const entries = useMemo<Entry[]>(() => {
    const shown =
      box !== "inbox" || mailOnly || scope === null || scope === undefined || activityItems === null
        ? []
        : activityInView(activityItems, { scope, filter: search.filter, kind: activityKind, unread });
    return mergeEntries(search.kind === "chat" || search.kind === "task" ? [] : visible, shown, moreMail);
  }, [box, mailOnly, scope, activityItems, search.filter, search.kind, activityKind, unread, visible, moreMail]);
  const openItem = search.a === undefined || activityItems === null ? undefined : activityItems.find((i) => i.id === search.a);
  const open = useThreadView(search.t);
  const workspace = useQuery(trpc.workspaces.get.queryOptions({ workspaceId }));

  const accountList: AccountView[] = accounts.data === undefined ? [] : accounts.data;
  const accountInfo = useMemo(() => new Map<string, AccountInfo>(accountList.map((a) => [a.id, { address: a.address, color: a.color }])), [accountList]);
  const defaultFrom = useMemo(() => {
    // The caller's own choice for this workspace (Settings → Mail accounts), per member.
    const configured = workspace.data?.defaultFromAccountId;
    if (configured !== undefined && configured !== null && accountList.some((a) => a.id === configured)) return configured;
    const inWorkspace = accountList.find((a) => a.workspaceId === workspaceId);
    if (inWorkspace !== undefined) return inWorkspace.id;
    const first = accountList[0];
    return first === undefined ? null : first.id;
  }, [workspace.data, accountList, workspaceId]);
  const tags = useMemo(() => [...new Set(visible.flatMap((t) => t.tags))].sort(), [visible]);
  const inScope = useMemo(() => {
    const ids = scope === null || scope === undefined ? null : scopeAccountIds(scope, accountList);
    return ids === null ? accountList : accountList.filter((a) => ids.includes(a.id));
  }, [scope, accountList]);
  const known = useAccountTags(inScope.map((a) => a.id));
  /** Tag and move suggestions for actions on threads of these accounts. */
  const suggest = useCallback(
    (picker: "tag" | "move", accountIds: readonly string[]) =>
      pickerSuggestions(
        picker,
        known,
        accountList.filter((a) => accountIds.includes(a.id)),
        tags,
      ),
    [known, accountList, tags],
  );
  const row: Thread | undefined = search.t === undefined ? undefined : visible.find((t) => t.id === search.t);
  // The bodies are fetched once (`staleTime: Infinity`), but the list row is live: when it names a
  // message the open view does not have (one just arrived), fetch the view again, once per change.
  const missingKey = useMemo(() => {
    if (row === undefined || open.data === undefined || open.data.id !== row.id) return null;
    const have = new Set(open.data.messages.map((m) => m.id));
    const missing = row.messageIds.filter((id) => !have.has(`${row.accountId}:${id}`));
    return missing.length === 0 ? null : `${row.id}|${row.messageIds.join(",")}`;
  }, [row, open.data]);
  const refetched = useRef<string | null>(null);
  const { refetch } = open;
  useEffect(() => {
    if (missingKey === null || refetched.current === missingKey) return;
    refetched.current = missingKey;
    void refetch();
  }, [missingKey, refetch]);

  return {
    scope,
    saved,
    threads,
    visible,
    entries,
    box,
    mailKind,
    source,
    /** The apps All mail is leaving out right now (null: none, or not All mail), and the switch. */
    hiding,
    setHidden,
    github,
    activity,
    openItem,
    actions,
    open,
    row,
    tags,
    suggest,
    accounts: accountList,
    accountsLoaded: accounts.isSuccess,
    accountInfo,
    defaultFrom,
    searchArgs: scope === null || scope === undefined ? {} : searchScope(scope, accountList),
  };
}

export type Inbox = ReturnType<typeof useInbox>;

/** The current time, refreshed every minute for relative dates. */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}
